import { BatteryChemistry } from '@brt/shared-types'

export interface BatteryPresetData {
  id: string
  name: string
  chemistry: BatteryChemistry
  nominalVoltage: number
  capacityAh: number
  cellVoltage: number
  cellsInSeries: number
  description: string
}

export const BATTERY_PRESETS: readonly BatteryPresetData[] = [
  {
    id: 'bat-18650',
    name: '18650 Li-Ion',
    chemistry: BatteryChemistry.LI_ION,
    nominalVoltage: 3.7,
    capacityAh: 3.4,
    cellVoltage: 3.7,
    cellsInSeries: 1,
    description: 'Standard 18650 cylindrical cell (3.4Ah typical)',
  },
  {
    id: 'bat-21700',
    name: '21700 Li-Ion',
    chemistry: BatteryChemistry.LI_ION,
    nominalVoltage: 3.7,
    capacityAh: 5.0,
    cellVoltage: 3.7,
    cellsInSeries: 1,
    description: '21700 cylindrical cell (5.0Ah typical)',
  },
  {
    id: 'bat-12v-7ah-sla',
    name: '12V 7Ah SLA',
    chemistry: BatteryChemistry.LEAD_ACID_FLOODED,
    nominalVoltage: 12,
    capacityAh: 7,
    cellVoltage: 2.0,
    cellsInSeries: 6,
    description: 'Common 12V 7Ah sealed lead-acid (alarm/UPS)',
  },
  {
    id: 'bat-12v-35ah-sla',
    name: '12V 35Ah SLA',
    chemistry: BatteryChemistry.LEAD_ACID_FLOODED,
    nominalVoltage: 12,
    capacityAh: 35,
    cellVoltage: 2.0,
    cellsInSeries: 6,
    description: '12V 35Ah deep-cycle lead-acid',
  },
  {
    id: 'bat-12v-100ah-lifepo4',
    name: '12V 100Ah LiFePO₄',
    chemistry: BatteryChemistry.LI_FEPO4,
    nominalVoltage: 12.8,
    capacityAh: 100,
    cellVoltage: 3.2,
    cellsInSeries: 4,
    description: 'Popular 12V 100Ah LiFePO₄ drop-in replacement',
  },
  {
    id: 'bat-24v-100ah-lifepo4',
    name: '24V 100Ah LiFePO₄',
    chemistry: BatteryChemistry.LI_FEPO4,
    nominalVoltage: 25.6,
    capacityAh: 100,
    cellVoltage: 3.2,
    cellsInSeries: 8,
    description: '24V 100Ah LiFePO₄ for solar/off-grid',
  },
  {
    id: 'bat-48v-100ah-lifepo4',
    name: '48V 100Ah LiFePO₄',
    chemistry: BatteryChemistry.LI_FEPO4,
    nominalVoltage: 51.2,
    capacityAh: 100,
    cellVoltage: 3.2,
    cellsInSeries: 16,
    description: '48V 100Ah LiFePO₄ server rack / solar',
  },
  {
    id: 'bat-12v-100ah-lead',
    name: '12V 100Ah Lead-Acid',
    chemistry: BatteryChemistry.LEAD_ACID_FLOODED,
    nominalVoltage: 12,
    capacityAh: 100,
    cellVoltage: 2.0,
    cellsInSeries: 6,
    description: 'Standard 12V 100Ah flooded lead-acid deep-cycle',
  },
  {
    id: 'bat-aa-alkaline',
    name: 'AA Alkaline',
    chemistry: BatteryChemistry.ALKALINE,
    nominalVoltage: 1.5,
    capacityAh: 2.8,
    cellVoltage: 1.5,
    cellsInSeries: 1,
    description: 'AA alkaline cell (LR6) ~2.8Ah at low drain',
  },
  {
    id: 'bat-aaa-alkaline',
    name: 'AAA Alkaline',
    chemistry: BatteryChemistry.ALKALINE,
    nominalVoltage: 1.5,
    capacityAh: 1.1,
    cellVoltage: 1.5,
    cellsInSeries: 1,
    description: 'AAA alkaline cell (LR03) ~1.1Ah at low drain',
  },
  {
    id: 'bat-9v-alkaline',
    name: '9V Alkaline',
    chemistry: BatteryChemistry.ALKALINE,
    nominalVoltage: 9,
    capacityAh: 0.6,
    cellVoltage: 1.5,
    cellsInSeries: 6,
    description: '9V alkaline (6LR61) ~600mAh',
  },
] as const

export function searchBatteryPresets(query: string, chemistry?: BatteryChemistry): BatteryPresetData[] {
  const q = query.toLowerCase().trim()
  return BATTERY_PRESETS.filter((p) => {
    const matchesChemistry = chemistry ? p.chemistry === chemistry : true
    const matchesQuery = q === '' || p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q)
    return matchesChemistry && matchesQuery
  })
}
